import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatWibJam, formatWibMenit, formatWibTanggal, hariWib, type TimelineSegment } from '../lib/api';
import { faktorRodaZoom } from '../lib/zoomGambar';
import { useBahasa } from '../i18n';

/**
 * Timeline ala editor non-linear (§G10 poin 3).
 *
 * Bekerja sepenuhnya di **jam dinding**, bukan waktu concat: itu sebabnya
 * bolong rekaman kelihatan sebagai celah, bukan dirapatkan (§D8.1). Pemetaan
 * ke posisi pemutar dikerjakan pemanggil, yang memang memegang daftar segmen.
 */

interface Props {
  /**
   * Batas **keras** sumbu — biasanya lantai retensi sampai sekarang (§D8.6).
   *
   * Dulu yang dipakai di sini rentang filter di atas halaman, dan akibatnya
   * timeline terkurung di dalam jam yang kebetulan diketik orang. Padahal
   * menggulung keluar dari jam itu justru gerakan yang paling wajar: kejadian
   * yang dicari sering berada sedikit di luar tebakan pertama. Sekarang sumbu
   * bebas bergerak di dalam batas ini, dan pemanggil yang menyesuaikan rentang
   * muatnya mengikuti.
   */
  minMs: number;
  maxMs: number;
  /** Rentang filter yang sedang termuat — digambar sebagai bayangan, bukan pagar. */
  fromMs: number;
  toMs: number;
  /** Jendela yang sedang terlihat (zoom & geser). */
  viewFromMs: number;
  viewToMs: number;
  onViewChange: (from: number, to: number) => void;
  /**
   * Playhead mulai / selesai diseret. Selama dipegang, pemanggil berhenti
   * menimpa posisinya dengan posisi video — kalau tidak, gagangnya melompat
   * bolak-balik antara tempat yang ditunjuk jari dan tempat video sedang
   * berada.
   */
  onSeret: (sedang: boolean) => void;
  segments: TimelineSegment[];
  /** Posisi playhead, jam dinding. `null` = belum ada yang diputar. */
  positionMs: number | null;
  onSeek: (ms: number) => void;
  markInMs: number | null;
  markOutMs: number | null;
  /** Bookmark milik akun ini di dalam rentang (§D8.7, §G10.7). */
  bookmarks: { id: string; atMs: number; label: string; expired: boolean }[];
  onPilihBookmark?: (atMs: number) => void;
  /** Tinggi pita segmen; bawaan `TINGGI_PITA`. Diatur garis pembatas di desktop. */
  tinggiPita?: number;
}

/** Tinggi pita segmen. Cukup tebal untuk jadi target sentuh (§G7 no.3). */
export const TINGGI_PITA = 44;
/**
 * Tinggi penggaris waktu di atas pita.
 *
 * Naik 22 → 28 pada 8 September 2026: penggaris sekarang membawa dua lapis —
 * angka di atas, gradasi tik di bawah (§G10.11 poin 2) — dan pada 22px keduanya
 * saling menempel sampai tidak ada yang terbaca.
 */
const TINGGI_PENGGARIS = 28;

/** Tinggi tik halus dan tik berlabel, diukur dari dasar penggaris. */
const TIK_KECIL = 4;
const TIK_BESAR = 8;

/**
 * Lebar zona di tepi jalur yang memicu geser otomatis saat playhead diseret
 * (§G10.11 poin 5).
 *
 * Dilaporkan lapangan: *"videohead kalo udah di-scrub oleh user lalu mentok
 * kanan/kiri layar, jangan diem aja dong, ikut geser ke kiri atau kanan
 * juga."* Benar — sebelum ini menyeret sampai tepi berarti playhead berhenti
 * di situ dan satu-satunya jalan melanjutkan adalah melepas jari, menggeser
 * sumbunya, lalu mulai menyeret lagi.
 *
 * 28px ≈ selebar ibu jari yang sudah setengah keluar dari layar: cukup lebar
 * untuk tersentuh tanpa disengaja dituju, cukup sempit untuk tidak menyalakan
 * geseran pada seretan yang memang berhenti di dekat tepi.
 */
const TEPI_GESER_PX = 28;

/**
 * Kecepatan geser otomatis, dalam **lebar jendela per detik** pada kedalaman
 * penuh.
 *
 * Relatif terhadap jendela, bukan piksel atau milidetik tetap: pada zoom 15
 * menit yang dicari orang detik, pada zoom seminggu yang dicari jam, dan
 * kecepatan tetap akan terasa merayap di satu ujung dan liar di ujung lain.
 */
const KECEPATAN_GESER = 0.6;

/**
 * Jarak antar label sumbu, dipilih dari tangga yang "bulat" bagi manusia.
 *
 * Bukan `rentang / 8`: itu menghasilkan label seperti 13:07:30 yang tidak
 * membantu siapa pun membaca posisi. Yang dicari label pada kelipatan yang
 * wajar — 5 detik, 30 detik, 5 menit, seperempat jam.
 */
const TANGGA_MS = [
  1_000, 5_000, 15_000, 30_000, 60_000, 5 * 60_000, 15 * 60_000, 30 * 60_000,
  60 * 60_000, 3 * 60 * 60_000, 6 * 60 * 60_000, 12 * 60 * 60_000, 24 * 60 * 60_000,
];

function langkahLabel(rentangMs: number, lebarPx: number): number {
  // Satu label per ±110px: lebih rapat dari itu dan angkanya saling menempel.
  const target = (rentangMs / Math.max(1, lebarPx)) * 110;
  return TANGGA_MS.find((t) => t >= target) ?? TANGGA_MS[TANGGA_MS.length - 1]!;
}

/**
 * Jarak antar **tik halus** — gradasi di antara dua angka (§G10.11 poin 2).
 *
 * Diminta lapangan: *"timeline ruler-nya dikasih skala, jadi orang
 * keliiatan"*. Sebelum ini penggaris cuma punya angka dengan satu garis di
 * pangkalnya, jadi posisi di antara dua angka harus ditaksir dengan mata —
 * dan pada sumbu enam jam, "di antara 03:00 dan 04:00" bisa berarti apa saja.
 *
 * Dipilih dari `TANGGA_MS` yang sama dengan labelnya, bukan hasil bagi seperti
 * `label / 10`: label 15 menit yang dibagi sepuluh menghasilkan tik tiap 1,5
 * menit, yaitu gradasi yang tidak bisa dipakai menghitung apa pun. Targetnya
 * ±8px — cukup rapat untuk terbaca sebagai skala, cukup renggang untuk tidak
 * berubah jadi arsiran abu-abu.
 */
function langkahTik(rentangMs: number, lebarPx: number, langkahLabelMs: number): number {
  const target = (rentangMs / Math.max(1, lebarPx)) * 8;
  const t = TANGGA_MS.find((v) => v >= target) ?? langkahLabelMs;
  return Math.min(t, langkahLabelMs);
}

export function PlaybackTimeline({
  minMs,
  maxMs,
  fromMs,
  toMs,
  viewFromMs,
  viewToMs,
  onViewChange,
  onSeret,
  segments,
  positionMs,
  onSeek,
  markInMs,
  markOutMs,
  bookmarks,
  onPilihBookmark,
  tinggiPita = TINGGI_PITA,
}: Props) {
  const { t } = useBahasa();
  const trackRef = useRef<HTMLDivElement>(null);
  const [lebar, setLebar] = useState(0);
  // Dipegang di ref, bukan state: dibaca di dalam handler pointer yang berjalan
  // puluhan kali per detik, dan render ulang di setiap gerakan tidak dibutuhkan.
  const geser = useRef<{
    jenis: 'scrub' | 'pan';
    xAwal: number;
    viewAwal: number;
    /**
     * Lebar jalur **saat seretan dimulai**, diukur ulang di situ juga.
     *
     * Bukan `lebar` dari state: nilai itu datang dari `ResizeObserver` dan bisa
     * masih 0 pada render pertama, sesudah halaman dilipat, atau sesudah keluar
     * dari layar penuh. Membagi dengan `Math.max(1, 0)` mengubah satu usapan
     * jari jadi lompatan berjam-jam — *"timeline-nya mental jauh"* (31 Agu
     * 2026, ponsel).
     */
    lebarAwal: number;
    rentangAwal: number;
  } | null>(null);

  /**
   * Cubit dua jari untuk zoom.
   *
   * Di desktop zoom punya dua jalan (roda tetikus dan `+`/`−`), di layar sentuh
   * sampai 31 Agu 2026 tidak punya satu pun — sumbunya terkunci di lebar yang
   * kebetulan sedang termuat, dan satu-satunya cara memperhalusnya adalah
   * mengetik ulang jam di atas halaman.
   */
  const cubit = useRef<{ jarak: number; pusatMs: number; rentangAwal: number } | null>(null);
  const jari = useRef(new Map<number, number>());

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setLebar(entry?.contentRect.width ?? 0));
    ro.observe(el);
    setLebar(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  const rentang = Math.max(1, viewToMs - viewFromMs);
  const xDari = useCallback((ms: number) => ((ms - viewFromMs) / rentang) * lebar, [viewFromMs, rentang, lebar]);
  const msDari = useCallback(
    (x: number) => viewFromMs + (Math.min(Math.max(x, 0), lebar) / Math.max(1, lebar)) * rentang,
    [viewFromMs, rentang, lebar],
  );

  /** Zoom di sekitar satu titik supaya yang sedang dilihat tidak lari. */
  const zoomKe = useCallback(
    (faktor: number, pusatMs: number) => {
      const rentangBaru = Math.min(maxMs - minMs, Math.max(2_000, rentang * faktor));
      const rasio = (pusatMs - viewFromMs) / rentang;
      let f = pusatMs - rasio * rentangBaru;
      let t = f + rentangBaru;
      if (f < minMs) [f, t] = [minMs, minMs + rentangBaru];
      if (t > maxMs) [f, t] = [maxMs - rentangBaru, maxMs];
      onViewChange(Math.max(minMs, f), Math.min(maxMs, t));
    },
    [minMs, maxMs, rentang, viewFromMs, onViewChange],
  );

  /**
   * Cermin prop yang selalu mutakhir, dibaca dari dalam gelung `requestAnimationFrame`.
   *
   * Gelung itu dipasang sekali per seretan dan hidup melewati puluhan render;
   * closure-nya karena itu memegang nilai dari render tempat ia lahir. Tanpa
   * cermin ini, geser otomatis akan menghitung memakai jendela yang sudah
   * ditinggalkannya sendiri satu frame yang lalu — dan hasilnya sumbu yang
   * bergetar maju-mundur.
   */
  const kini = useRef({ viewFromMs, rentang, minMs, maxMs, lebar, onViewChange, onSeek });
  kini.current = { viewFromMs, rentang, minMs, maxMs, lebar, onViewChange, onSeek };

  /**
   * Geser otomatis saat playhead diseret sampai tepi (§G10.11 poin 5).
   *
   * `x` disimpan di ref dan bukan diambil dari kejadian pointer: selama jari
   * **berhenti** di tepi tidak ada satu pun `pointermove` yang datang, padahal
   * justru itu keadaan yang harus terus menggeser. Yang menggerakkan waktu di
   * sini rAF, bukan gerakan jari.
   */
  const auto = useRef<{ x: number; raf: number | null; sebelum: number } | null>(null);

  const hentikanAuto = useCallback(() => {
    const a = auto.current;
    if (a?.raf !== null && a?.raf !== undefined) cancelAnimationFrame(a.raf);
    auto.current = null;
  }, []);

  const langkahAuto = useCallback(
    (waktu: number) => {
      const a = auto.current;
      const el = trackRef.current;
      if (!a || !el) return;
      a.raf = requestAnimationFrame(langkahAuto);

      const r = el.getBoundingClientRect();
      // Dijepit 50ms: tab yang sempat tersembunyi mengirim satu selisih raksasa
      // pada frame pertama sesudah kembali, dan tanpa jepitan ini sumbunya
      // melompat berjam-jam dalam satu kedipan.
      const dt = Math.min(50, Math.max(0, waktu - a.sebelum)) / 1000;
      a.sebelum = waktu;

      const dariKiri = a.x - r.left;
      const dariKanan = r.right - a.x;
      let arah = 0;
      let dalam = 0;
      if (dariKiri < TEPI_GESER_PX) {
        arah = -1;
        dalam = (TEPI_GESER_PX - dariKiri) / TEPI_GESER_PX;
      } else if (dariKanan < TEPI_GESER_PX) {
        arah = 1;
        dalam = (TEPI_GESER_PX - dariKanan) / TEPI_GESER_PX;
      }
      if (arah === 0) return;

      const c = kini.current;
      // Lantai 0,15 supaya jari yang berhenti persis di garis batas zona tetap
      // menggeser, bukan menggantung pada kecepatan nol.
      const laju = c.rentang * KECEPATAN_GESER * Math.min(1, Math.max(0.15, dalam));
      let f = c.viewFromMs + arah * laju * dt;
      f = Math.min(Math.max(f, c.minMs), Math.max(c.minMs, c.maxMs - c.rentang));
      if (f === c.viewFromMs) return; // Sudah mentok di batas keras sumbu.
      c.onViewChange(f, f + c.rentang);

      // Playhead tetap berada di bawah jari — dihitung pada jendela yang BARU.
      // Kalau tidak, ia menempel di tepi sementara waktunya berjalan, dan yang
      // terlihat adalah sumbu yang bergerak tanpa membawa apa pun.
      const x = Math.min(Math.max(a.x - r.left, 0), c.lebar);
      c.onSeek(f + (x / Math.max(1, c.lebar)) * c.rentang);
    },
    [],
  );

  const jalankanAuto = useCallback(
    (clientX: number) => {
      if (auto.current) {
        auto.current.x = clientX;
        return;
      }
      auto.current = { x: clientX, raf: null, sebelum: performance.now() };
      auto.current.raf = requestAnimationFrame(langkahAuto);
    },
    [langkahAuto],
  );

  // Seretan yang belum selesai saat komponen dilepas tidak boleh meninggalkan
  // rAF yang masih memanggil `onSeek` ke halaman yang sudah pergi.
  useEffect(() => hentikanAuto, [hentikanAuto]);

  // `passive: false` wajib: handler ini memanggil `preventDefault()` supaya roda
  // mouse men-zoom timeline, bukan menggulung halaman. React memasang listener
  // wheel sebagai passive, jadi tidak bisa lewat prop `onWheel`.
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      /**
       * Langkahnya mengikuti besar `deltaY`, bukan satu langkah tetap per
       * kejadian — dan memakai penakar yang sama dengan zoom gambar (§G10.6),
       * jadi roda tetikus terasa sama di sini dan di sana.
       *
       * Bentuk lamanya (`deltaY > 0 ? 1,25 : 0,8`) benar untuk roda tetikus yang
       * mengirim satu kejadian per klik, tapi liar di touchpad: gulir dua jari
       * mengirim ±60 kejadian per detik, dan 0,8 pangkat 60 sampai di batas
       * zoom sebelum jarinya sempat berhenti.
       *
       * Dibalik karena arahnya memang berlawanan: di sini `faktor` mengalikan
       * **rentang waktu**, jadi faktor kecil berarti jendela menyempit — zoom
       * masuk.
       */
      zoomKe(1 / faktorRodaZoom(e), msDari(e.clientX - rect.left));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomKe, msDari]);

  /**
   * Tiga gerakan, tiga tempat — dan itu yang dulu rancu (§D8.6).
   *
   * Sebelumnya menggeser playhead berarti menyeret **penggaris setinggi 22px**
   * di atas pita, sementara pita itu sendiri memindahkan jendela. Dua gerakan
   * yang terasa mirip pada dua jalur setebal jari, tanpa satu pun pegangan yang
   * kelihatan bisa dipegang: yang ingin memindahkan playhead malah menggeser
   * jendela, dan sebaliknya.
   *
   * Sekarang:
   *
   * | Di mana | Diseret | Diklik saja |
   * |---|---|---|
   * | Playhead (pegangan merah) | pindahkan playhead | — |
   * | Penggaris atas | pindahkan playhead (scrub) | pindahkan playhead |
   * | Pita segmen | geser jendela | **pindahkan playhead** |
   *
   * Baris terakhir yang membuatnya tidak terasa mati: menyeret pita memang
   * menggeser jendela, tapi klik tanpa gerakan tetap berarti "ke sini" — dan
   * itu yang dilakukan orang tanpa diajari.
   */
  const GESER_MINIMAL_PX = 4;

  function pointerDown(e: React.PointerEvent, jenis: 'scrub' | 'pan') {
    const rect = trackRef.current!.getBoundingClientRect();
    e.currentTarget.setPointerCapture(e.pointerId);
    jari.current.set(e.pointerId, e.clientX);

    // Jari kedua mendarat: yang sedang berjalan bukan lagi geseran, melainkan
    // cubitan. Geserannya dibatalkan supaya sumbunya tidak digeser dan
    // di-zoom sekaligus.
    if (jari.current.size === 2) {
      const [x1, x2] = [...jari.current.values()];
      const kiri = Math.min(x1!, x2!) - rect.left;
      const kanan = Math.max(x1!, x2!) - rect.left;
      geser.current = null;
      hentikanAuto();
      cubit.current = {
        jarak: Math.max(1, kanan - kiri),
        pusatMs: msDari((kiri + kanan) / 2),
        rentangAwal: rentang,
      };
      return;
    }

    if (jenis === 'scrub') {
      onSeret(true);
      onSeek(msDari(e.clientX - rect.left));
    }
    geser.current = {
      jenis,
      xAwal: e.clientX,
      viewAwal: viewFromMs,
      lebarAwal: Math.max(1, rect.width),
      rentangAwal: rentang,
    };
  }

  function pointerMove(e: React.PointerEvent) {
    if (jari.current.has(e.pointerId)) jari.current.set(e.pointerId, e.clientX);

    const c = cubit.current;
    if (c && jari.current.size >= 2) {
      const [x1, x2] = [...jari.current.values()];
      const jarak = Math.max(1, Math.abs(x1! - x2!));
      const rentangBaru = c.rentangAwal * (c.jarak / jarak);
      zoomKe(rentangBaru / rentang, c.pusatMs);
      return;
    }

    const g = geser.current;
    if (!g) return;
    const rect = trackRef.current!.getBoundingClientRect();
    if (g.jenis === 'scrub') {
      onSeek(msDari(e.clientX - rect.left));
      // Di tepi, seretan berhenti jadi "pindahkan playhead" dan jadi
      // "bawa sumbunya ikut" — gelung rAF yang mengerjakannya, karena jari
      // yang diam di tepi tidak mengirim kejadian apa pun (§G10.11 poin 5).
      const diTepi =
        e.clientX - rect.left < TEPI_GESER_PX || rect.right - e.clientX < TEPI_GESER_PX;
      if (diTepi) jalankanAuto(e.clientX);
      else hentikanAuto();
      return;
    }
    const deltaMs = ((g.xAwal - e.clientX) / g.lebarAwal) * g.rentangAwal;
    // Dibatasi batas keras sumbu, bukan rentang filter: menggulung keluar dari
    // jam yang diketik di atas memang gerakan yang diizinkan sekarang.
    //
    // Rentangnya ikut dijepit ke lebar sumbu. Kalau jendela yang terlihat lebih
    // lebar daripada seluruh sumbu — mudah terjadi begitu rentang filter
    // membengkak — `maxMs - rentang` jatuh di sebelah kiri `minMs`, kedua
    // penjepit di bawah saling bertabrakan, dan hasilnya selalu angka yang sama:
    // sumbu yang terlihat mati total, persis laporan *"nggak bisa geser
    // timeline"* dari ponsel.
    const r = Math.min(g.rentangAwal, Math.max(1, maxMs - minMs));
    let f = g.viewAwal + deltaMs;
    f = Math.min(Math.max(f, minMs), Math.max(minMs, maxMs - r));
    onViewChange(f, f + r);
  }

  function pointerUp(e: React.PointerEvent) {
    hentikanAuto();
    const g = geser.current;
    geser.current = null;
    jari.current.delete(e.pointerId);
    if (jari.current.size < 2) cubit.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
    if (g?.jenis === 'scrub') onSeret(false);
    if (!g || g.jenis !== 'pan') return;
    // Geseran yang lebih pendek dari ambang ini bukan geseran — itu klik yang
    // tangannya sedikit bergoyang.
    if (Math.abs(e.clientX - g.xAwal) > GESER_MINIMAL_PX) return;
    const rect = trackRef.current!.getBoundingClientRect();
    onSeek(msDari(e.clientX - rect.left));
    // Klik di pita: sekali pindah, bukan seretan — tapi pemanggil tetap perlu
    // tahu bahwa posisinya baru saja ditentukan orang.
    onSeret(false);
  }

  const langkah = langkahLabel(rentang, lebar);
  const tanda: number[] = [];
  for (let t = Math.ceil(viewFromMs / langkah) * langkah; t <= viewToMs; t += langkah) tanda.push(t);

  /**
   * Gradasi tik halus, digambar sebagai **satu gradien berulang** dan bukan
   * ratusan elemen.
   *
   * Pada jalur selebar 1400px dengan tik tiap 8px itu 175 `<span>` yang lahir
   * dan mati di setiap frame geseran — di halaman yang juga sedang men-dekode
   * video. Gradien tidak menambah satu simpul pun ke pohon dokumen, dan
   * garisnya justru lebih tajam karena tidak melewati pembulatan tata letak.
   *
   * `backgroundPositionX` menggeser polanya supaya tik pertama jatuh pada
   * kelipatan waktu yang bulat — tanpa itu, skalanya rapi tapi menunjuk angka
   * yang salah, dan skala yang berbohong lebih buruk daripada tidak ada skala.
   */
  const tikMs = langkahTik(rentang, lebar, langkah);
  const tikPx = (tikMs / rentang) * lebar;
  const tikGeserPx = lebar > 0 ? xDari(Math.ceil(viewFromMs / tikMs) * tikMs) : 0;
  // Di bawah 3px gradasinya berhenti jadi skala dan jadi arsiran rata.
  const adaTik = lebar > 0 && tikPx >= 3;

  /**
   * Bookmark yang berdempetan **digabung**, bukan ditumpuk (§G10.7).
   *
   * Ambangnya piksel, bukan detik: yang menentukan bisa-tidaknya diklik adalah
   * jarak di layar, dan jarak itu berubah setiap kali orang men-zoom. Dua belas
   * piksel ≈ lebar target sentuh terkecil yang masih bisa dibedakan mata dari
   * penanda sebelahnya.
   */
  const rumpunBookmark = useMemo(() => {
    const urut = [...bookmarks].sort((a, b) => a.atMs - b.atMs);
    const out: { kunci: string; atMs: number; jumlah: number; judul: string; expired: boolean }[] = [];
    for (const b of urut) {
      const terakhir = out[out.length - 1];
      if (terakhir && Math.abs(xDari(b.atMs) - xDari(terakhir.atMs)) < 12) {
        terakhir.jumlah += 1;
        terakhir.judul += `\n${formatWibJam(b.atMs)} — ${b.label}`;
        // Rumpun yang salah satu isinya masih punya gambar tidak diredupkan:
        // yang diklik orang adalah rumpunnya, dan di dalamnya ada yang bisa
        // dituju.
        terakhir.expired = terakhir.expired && b.expired;
        continue;
      }
      out.push({
        kunci: b.id,
        atMs: b.atMs,
        jumlah: 1,
        judul: `${formatWibJam(b.atMs)} — ${b.label}`,
        expired: b.expired,
      });
    }
    return out;
  }, [bookmarks, xDari]);

  const inX = markInMs === null ? null : xDari(markInMs);
  const outX = markOutMs === null ? null : xDari(markOutMs);
  const posX = positionMs === null ? null : xDari(positionMs);
  /** Seleksi utuh — hanya keadaan ini yang meredupkan sekelilingnya. */
  const seleksiPenuh = inX !== null && outX !== null && outX > inX;

  return (
    <div className="select-none rounded-lg border border-line bg-surface">
      {/* `overflow-hidden` bukan hiasan: label penggaris dan pegangan playhead
          diletakkan `absolute` pada posisi piksel, dan yang paling kanan selalu
          menjulur melewati tepi jalur — label yang membawa tanggal bisa 110px
          lebarnya. Elemen absolut tidak menambah lebar induknya, tapi ia tetap
          menumbuhkan **scrollable overflow** di wadah gulung terdekat, dan
          `<main>` (yang `overflow-y-auto`) menjawabnya dengan scrollbar
          horizontal di seluruh halaman. Dilaporkan 31 Agu 2026 di 1920×1080:
          hilang hanya kalau Chrome di-zoom 80%. */}
      <div ref={trackRef} className="relative touch-none overflow-hidden">
        {/* Penggaris — di sinilah scrub, mengikuti kebiasaan NLE. */}
        <div
          className="relative touch-none cursor-ew-resize border-b border-line bg-subtle"
          style={{ height: TINGGI_PENGGARIS }}
          onPointerDown={(e) => pointerDown(e, 'scrub')}
          onPointerMove={pointerMove}
          onPointerUp={pointerUp}
          onPointerCancel={pointerUp}
        >
          {/* Gradasi halus di dasar penggaris (§G10.11 poin 2). */}
          {adaTik && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 bottom-0 text-muted opacity-70"
              style={{
                height: TIK_KECIL,
                backgroundImage: `repeating-linear-gradient(to right, currentColor 0 1px, transparent 1px ${tikPx}px)`,
                backgroundPositionX: `${tikGeserPx}px`,
              }}
            />
          )}

          {tanda.map((tt) => {
            // Label yang pangkalnya sudah di ujung kanan tidak digambar sama
            // sekali: yang terlihat cuma potongan satu-dua huruf, dan
            // memotongnya lebih rapi daripada menampilkan "1" dari "19:20".
            if (lebar > 0 && xDari(tt) > lebar - 34) return null;
            // Label pertama sesudah tengah malam membawa tanggalnya. Jam tanpa
            // tanggal ambigu begitu sumbunya melewati tengah malam — rekaman
            // tujuh hari punya tujuh "19:20" yang kelihatan persis sama.
            const hariBaru = hariWib(tt) !== hariWib(tt - langkah);
            return (
              <span
                key={tt}
                className="pointer-events-none absolute top-0"
                style={{ left: xDari(tt), height: TINGGI_PENGGARIS }}
              >
                {/* Angkanya di atas, tik berlabelnya di dasar — dua lapis, sama
                    seperti penggaris NLE mana pun. Sebelum ini angka dan garis
                    menempel jadi satu benda setinggi penggaris, dan pada zoom
                    lebar deretan garis penuh itu terbaca sebagai pagar, bukan
                    skala. */}
                <span
                  className={`block pl-1 text-[10px] leading-[16px] tabular-nums ${
                    hariBaru ? 'font-semibold text-text' : 'text-muted'
                  }`}
                >
                  {langkah < 60_000 ? formatWibJam(tt) : formatWibMenit(tt)}
                  {hariBaru && <span className="ml-1">{formatWibTanggal(tt)}</span>}
                </span>
                <span
                  aria-hidden="true"
                  className={`absolute bottom-0 left-0 w-px ${
                    hariBaru ? 'bg-accent' : 'bg-[var(--color-text-muted)]'
                  }`}
                  style={{ height: TIK_BESAR }}
                />
              </span>
            );
          })}

          {/* Penanda (§G10.7, §G10.11 poin 4) — segitiga menghadap ke atas yang
              duduk di dasar penggaris, mengikuti bentuk *mark point* di
              referensi lapangan. Sebelum ini ia balok setinggi seluruh pita,
              dan balok setinggi itu bersaing dengan playhead untuk hal yang
              justru bukan posisi sekarang.

              Tidak ada garis yang menjulur ke bawah dengan sengaja: penanda
              adalah satu titik waktu, dan ia sudah berdiri tepat di atas kolom
              pita yang dimaksudnya.

              Yang berdempetan digabung jadi satu bertuliskan jumlahnya, bukan
              ditumpuk: tiga penanda dalam satu piksel yang saling menimpa
              membuat dua di antaranya mustahil diklik, dan yang paling atas
              menang tanpa alasan yang bisa dijelaskan. */}
          {rumpunBookmark.map((r) => (
            <button
              key={r.kunci}
              type="button"
              // `pointerdown` ditahan supaya menekan penanda tidak ikut memulai
              // scrub di penggaris di bawahnya — kalau tidak, playhead melompat
              // ke tempat jari mendarat sebelum penandanya sempat terbaca.
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onPilihBookmark?.(r.atMs);
              }}
              title={r.judul}
              aria-label={r.judul}
              className="absolute bottom-0 z-[4] flex h-3.5 w-4 -translate-x-1/2 items-end justify-center"
              style={{ left: xDari(r.atMs) }}
            >
              <svg
                viewBox="0 0 12 8"
                className={`block h-2 w-3 ${
                  r.expired ? 'text-[var(--color-text-muted)] opacity-60' : 'text-accent-highlight'
                }`}
                aria-hidden="true"
              >
                <path d="M6 0 12 8H0z" fill="currentColor" />
              </svg>
              {r.jumlah > 1 && (
                <span className="absolute -top-1 left-1/2 -translate-x-1/2 rounded bg-accent-highlight px-1 text-[10px] leading-[14px] text-white">
                  {r.jumlah}
                </span>
              )}
            </button>
          ))}

          {/* Tanggal yang sedang dilihat, menempel di tepi kiri. Menutupi label
              pertama dengan sengaja: tanpa jangkar ini, sumbu yang tidak memuat
              satu pun pergantian hari sama sekali tidak menyebutkan tanggal. */}
          <span className="pointer-events-none absolute left-0 top-0 z-[1] bg-subtle pr-2 pl-1 text-[10px] font-semibold leading-[16px] text-text">
            {formatWibTanggal(viewFromMs)}
          </span>
        </div>

        {/* Pita segmen — geser di sini memindahkan jendela, bukan playhead. */}
        <div
          className="relative touch-none overflow-hidden bg-page cursor-grab active:cursor-grabbing"
          style={{ height: tinggiPita }}
          onPointerDown={(e) => pointerDown(e, 'pan')}
          onPointerMove={pointerMove}
          onPointerUp={pointerUp}
          onPointerCancel={pointerUp}
        >
          {/* Blok rekaman. Yang TIDAK digambar adalah bolongnya — celah kosong
              itu justru informasinya (§D8.1). */}
          {segments.map((s) => {
            const a = new Date(s.startedAt).getTime();
            const b = new Date(s.endedAt).getTime();
            if (b < viewFromMs || a > viewToMs) return null;
            const kiri = xDari(a);
            const lebar = Math.max(1, xDari(b) - kiri);
            /**
             * Batas potongan digambar hanya kalau bloknya cukup lebar.
             *
             * Diminta lapangan 8 Sep 2026: *"di setiap clip bisa gak sih dikasih
             * gambar start dan end clip? Jadi kita tau kapan potongannya?"* —
             * dan pertanyaan di belakangnya lebih penting daripada permintaannya
             * sendiri: *"sebenernya timeline-nya itu bergerak gak sih?"*.
             * Segmen yang bersambungan menempel tanpa jeda, jadi tanpa garis ini
             * empat jam rekaman terlihat sebagai satu balok yang tidak pernah
             * berubah apa pun.
             *
             * Ambang 8 piksel, dan di bawah itu sengaja tidak digambar: pada
             * zoom lebar satu segmen 15 menit cuma beberapa piksel, dan garis
             * setebal bloknya sendiri berhenti menjadi batas — ia menjadi
             * seluruh isinya, dan pita rekaman berubah jadi pagar. Orangnya
             * sendiri yang bilang itu tidak apa-apa.
             */
            const cukupLebar = lebar >= 8;
            return (
              <div
                key={s.id}
                // Warna solid, bukan `bg-accent/70`: token warna di
                // `tailwind.config.js` berupa `var(--color-…)` polos tanpa
                // `<alpha-value>`, jadi modifier opasitas tidak menghasilkan
                // kelas apa pun — terverifikasi 21 Agu 2026, pita segmennya
                // tidak tergambar sama sekali dan timeline tampak kosong.
                //
                // Alasan yang sama membuat garis batas di bawah memakai `style`
                // dengan `rgba()` literal, bukan kelas beropasitas.
                className="pointer-events-none absolute top-2 overflow-hidden rounded-sm bg-accent"
                style={{ left: kiri, width: lebar, height: tinggiPita - 16 }}
                title={`${formatWibJam(a)} – ${formatWibJam(b)}`}
              >
                {cukupLebar && (
                  <>
                    <span
                      className="absolute inset-y-0 left-0 w-px"
                      style={{ backgroundColor: 'rgba(255,255,255,0.85)' }}
                    />
                    <span
                      className="absolute inset-y-0 right-0 w-px"
                      style={{ backgroundColor: 'rgba(0,0,0,0.35)' }}
                    />
                  </>
                )}
              </div>
            );
          })}

          {/* Bayangan di luar rentang yang diminta di atas halaman. Bukan pagar
              lagi — cuma penanda "yang ini yang kamu minta", supaya menggulung
              keluar darinya tetap terasa berada di suatu tempat (§D8.6). */}
          {xDari(fromMs) > 0 && (
            <div
              className="pointer-events-none absolute inset-y-0 left-0 bg-page/70"
              style={{ width: Math.min(lebar, xDari(fromMs)) }}
            />
          )}
          {xDari(toMs) < lebar && (
            <div
              className="pointer-events-none absolute inset-y-0 right-0 bg-page/70"
              style={{ width: Math.min(lebar, lebar - xDari(toMs)) }}
            />
          )}

        </div>

        {/* ── Mark in / out: yang di luar diredupkan (§G10.11 poin 3) ──────────

            Diminta lapangan mengikuti referensi NLE: *"mark in mark out-nya
            buat sistem gelap terang kayak gitu juga."* Sebelum ini yang
            **terpilih** justru diberi warna (kotak kuning transparan) dan
            sisanya dibiarkan biasa — arah yang terbalik. Menyorot yang dipilih
            berarti menambah satu lapisan di atas hal yang justru sedang
            diperiksa orang; meredupkan yang tidak dipilih membuang perhatian
            dari sekitarnya tanpa menyentuh apa pun yang dilihat.

            **Hanya kalau keduanya sudah dipasang.** Menekan `I` saja tidak
            boleh langsung menggelapkan separuh timeline: pada saat itu belum
            ada yang terpilih, cuma ada satu batas — dan setengah layar yang
            mendadak gelap terbaca sebagai kerusakan, bukan sebagai seleksi.

            Redupnya menembus penggaris DAN pita sekaligus, karena itu ia
            tinggal di wadah luar dan bukan di dalam salah satunya. */}
        {seleksiPenuh && inX! > 0 && (
          <div
            className="pointer-events-none absolute inset-y-0 left-0 z-[5]"
            style={{ width: Math.min(lebar, inX!), backgroundColor: 'rgba(0,0,0,0.34)' }}
            aria-hidden="true"
          />
        )}
        {seleksiPenuh && outX! < lebar && (
          <div
            className="pointer-events-none absolute inset-y-0 z-[5]"
            style={{
              left: Math.max(0, outX!),
              width: Math.max(0, lebar - outX!),
              backgroundColor: 'rgba(0,0,0,0.34)',
            }}
            aria-hidden="true"
          />
        )}

        {/* Bilah seleksi di puncak penggaris — bagian (1) di gambar referensi.
            Ia yang membuat rentang terpilih terbaca sebagai satu benda utuh,
            bukan dua garis yang kebetulan berdiri berjauhan. */}
        {seleksiPenuh && (
          <div
            className="pointer-events-none absolute top-0 z-[6] h-[3px] bg-amber-500"
            style={{ left: inX!, width: Math.max(1, outX! - inX!) }}
            aria-hidden="true"
          />
        )}

        {/* Batas in/out: garis penuh + gagang siku di dasar penggaris — bagian
            (3) di gambar referensi.

            Garisnya berada DI ATAS redup (z-6 lawan z-5): batas seleksi adalah
            satu-satunya hal yang tidak boleh ikut tenggelam bersama yang
            diredupkannya sendiri. */}
        {inX !== null && (
          <>
            <div
              className="pointer-events-none absolute inset-y-0 z-[6] w-0.5 bg-amber-500"
              style={{ left: inX }}
              aria-hidden="true"
            />
            <svg
              viewBox="0 0 9 12"
              className="pointer-events-none absolute z-[6] block h-3 w-[9px] text-amber-500"
              style={{ left: inX, top: TINGGI_PENGGARIS - 12 }}
              aria-hidden="true"
            >
              <path d="M0 0v12h9V9.5H2.5V0z" fill="currentColor" />
            </svg>
          </>
        )}
        {outX !== null && (
          <>
            <div
              className="pointer-events-none absolute inset-y-0 z-[6] w-0.5 bg-amber-500"
              style={{ left: outX }}
              aria-hidden="true"
            />
            <svg
              viewBox="0 0 9 12"
              className="pointer-events-none absolute z-[6] block h-3 w-[9px] text-amber-500"
              style={{ left: outX - 9, top: TINGGI_PENGGARIS - 12, transform: 'scaleX(-1)' }}
              aria-hidden="true"
            >
              <path d="M0 0v12h9V9.5H2.5V0z" fill="currentColor" />
            </svg>
          </>
        )}

        {/* Pegangan playhead — elemen tersendiri di atas kedua jalur, bukan
            hiasan di ujung garis merah. Inilah yang membuat "seret playhead"
            punya sasaran yang kelihatan bisa dipegang (§D8.6). Lebarnya 20px
            supaya bisa dikenai jari, meski garisnya sendiri 2px.

            **Bentuknya segitiga menghadap ke bawah sejak 8 September 2026**
            (§G10.11 poin 1), mengikuti gambar referensi. Yang digantikan wajik
            45° — bentuk yang tidak menunjuk ke mana pun, jadi pada zoom lebar
            tidak jelas sisi mananya yang berarti "sekarang". Segitiga punya
            satu titik, dan titik itu duduk persis di atas garisnya.

            Garis merahnya sekarang ikut di dalam pegangan ini, bukan elemen
            terpisah di dalam pita: satu benda, satu posisi, dan tidak ada lagi
            kemungkinan kepala dan garisnya menunjuk dua piksel yang berbeda. */}
        {posX !== null && (
          <div
            role="slider"
            aria-label={t('Playhead')}
            aria-valuemin={viewFromMs}
            aria-valuemax={viewToMs}
            aria-valuenow={positionMs ?? viewFromMs}
            aria-valuetext={formatWibJam(positionMs ?? viewFromMs)}
            tabIndex={0}
            onPointerDown={(e) => {
              e.stopPropagation();
              pointerDown(e, 'scrub');
            }}
            onPointerMove={pointerMove}
            onPointerUp={pointerUp}
            onPointerCancel={pointerUp}
            className="absolute top-0 z-20 flex w-5 -translate-x-1/2 cursor-ew-resize flex-col items-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1"
            style={{ left: posX, height: TINGGI_PENGGARIS + tinggiPita }}
          >
            {/* Garis putih tipis mengelilingi segitiganya: kepala merah polos
                hilang di atas pita rekaman yang gelap pada tema gelap, dan
                hilang lagi di atas celah terang pada tema terang. */}
            <svg
              viewBox="0 0 12 9"
              className="block h-[9px] w-3 shrink-0 text-red-600 drop-shadow"
              aria-hidden="true"
            >
              <path
                d="M0.5 0.5h11L6 8.5z"
                fill="currentColor"
                stroke="rgba(255,255,255,0.9)"
                strokeWidth="1"
                strokeLinejoin="round"
              />
            </svg>
            <span className="w-0.5 flex-1 bg-red-600" aria-hidden="true" />
          </div>
        )}
      </div>
    </div>
  );
}
